import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  CreditCard,
  Clock,
  Key,
  Settings,
  FileText,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { path: "/payments", label: "Payments", icon: CreditCard, testId: "nav-payments" },
  { path: "/history", label: "History", icon: Clock, testId: "nav-history" },
  { path: "/api-keys", label: "API Keys", icon: Key, testId: "nav-api-keys" },
  { path: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
  { path: "/api-docs", label: "API Docs", icon: FileText, testId: "nav-api-docs" },
];

export default function Layout({ children }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigate = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.95),_rgba(243,244,246,0.92)_34%,_rgba(228,231,236,0.9)_100%)] text-foreground">
      <div className="lg:hidden sticky top-0 z-40 border-b border-border/70 bg-background/90 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
          <div>
            <h2 className="font-heading text-base font-black tracking-tight">GOPAY GW</h2>
            <p className="text-[11px] text-muted-foreground">Admin workspace</p>
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-border bg-card shadow-sm"
            aria-label="Buka menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Tutup menu"
            onClick={() => setMobileMenuOpen(false)}
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
          />
          <aside className="absolute inset-y-0 left-0 flex w-[86vw] max-w-sm flex-col border-r border-border bg-card p-4 shadow-2xl">
            <div className="flex items-start justify-between border-b border-border pb-4">
              <div>
                <h2 className="font-heading text-lg font-black tracking-tight">GOPAY GW</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Payment Gateway</p>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-border"
                aria-label="Tutup menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <nav className="flex-1 py-3 overflow-y-auto">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  data-testid={item.testId}
                  onClick={handleNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm transition-colors duration-150 ${
                      isActive
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`
                  }
                >
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="border-t border-border pt-3">
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      <aside className="fixed inset-y-0 left-0 hidden w-72 flex-col border-r border-border/70 bg-card/90 p-4 shadow-[0_0_0_1px_rgba(15,23,42,0.04)] backdrop-blur lg:flex">
        <div className="rounded-3xl border border-border/70 bg-slate-950 px-5 py-4 text-white shadow-lg">
          <h2 className="font-heading text-lg font-black tracking-tight">GOPAY GW</h2>
          <p className="text-xs text-white/70 mt-0.5">Payment workspace</p>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="rounded-3xl border border-border/70 bg-background/80 p-3">
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 lg:pl-72">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</div>
      </main>
    </div>
  );
}
